// @flow
import { useContext } from 'react';
import styled from '@emotion/styled';
import { SPACING, typography } from '@tablekit/theme';
import { PageContext } from 'components/PageContext';
import type {
  BlokComponentPropsType,
  BlogAggregateCategoryType
} from 'components/types';
import { CallToActionBackground } from 'components/CallToAction';
import { MOBILE_BREAKPOINT } from 'utils/theme';
import Title from 'styled/Title';
import CoreBlogPreview from '../Preview';
import {
  Grid,
  GridOuter,
  CARD_FORMATS,
  CardTitle,
  CardText,
  CardAuthor,
  CardPersonImage,
  CardFooter
} from '../styled';
import Pagination from './Pagination';

const Wrapper = styled.div`
  margin: ${SPACING.L8} 0 ${SPACING.L9};

  @media ${MOBILE_BREAKPOINT} {
    margin: ${SPACING.L6} 0 ${SPACING.L8};
  }
`;

const HeaderTitle = styled(Title)`
  -ms-grid-column-span: 9;
  grid-column-end: span 9;
  margin-bottom: ${SPACING.L8};
`;

const HeaderGrid = styled(GridOuter)`
  @media ${MOBILE_BREAKPOINT} {
    display: none;
  }
`;

const BlogPreview = styled(CoreBlogPreview)`
  @media ${MOBILE_BREAKPOINT} {
    margin: ${SPACING.L6} 0 0;

    ${CardPersonImage} {
      display: none;
    }

    ${CardTitle} {
      margin-bottom: ${SPACING.L2};
    }

    ${CardText} {
      display: flex;
      margin-bottom: ${SPACING.L5};
    }

    ${CardAuthor} {
      ${typography('L1')};
    }

    ${CardFooter} {
      margin: 0;
    }
  }
`;

const BlogAggregatePage = ({
  blok: {
    title,
    show_author: showAuthor,
    show_categories: showCategories,
    show_preview_text: showPreviewText,
    show_published_date: showPublishedDate,
    show_read_time: showReadTime
  }
}: BlokComponentPropsType<BlogAggregateCategoryType>) => {
  const pageContext = useContext(PageContext);
  if (!pageContext || !pageContext.pageBlogs) {
    return <div>Page has been incorrectly configured</div>;
  }
  const { currentPage = 0, countPages = 0, story, pageBlogs } = pageContext;
  return (
    <Wrapper>
      <HeaderGrid>
        {currentPage === 1 && <HeaderTitle align="left">{title}</HeaderTitle>}
      </HeaderGrid>
      <Grid rows={3}>
        {pageBlogs.map(
          (
            {
              uuid,
              full_slug: fullSlug,
              first_published_at: publishedDate,
              content: {
                author,
                categories,
                hero_title: blogTitle,
                preview_image: image,
                preview_text: text,
                read_time: readTime
              }
            },
            index
          ) => {
            let format = CARD_FORMATS.THIRD;
            const msStyles = {};
            if (currentPage === 1 && index <= 2) {
              if (index === 0) {
                format = CARD_FORMATS.FULL;
                msStyles.msGridRow = '1';
                msStyles.msGridColumn = '1';
                msStyles.msGridColumnSpan = '6';
              } else {
                format = CARD_FORMATS.HALF;
                msStyles.msGridRow = '2';
                msStyles.msGridColumn = index === 1 ? '1' : '4';
                msStyles.msGridColumnSpan = '3';
              }
            } else {
              if (currentPage > 1) {
                msStyles.msGridRow = `${Math.ceil((index + 1) / 3)}`;
              } else {
                msStyles.msGridRow = '3';
              }
              msStyles.msGridColumn = `${(index % 3) * 2 + 1}`;
              msStyles.msGridColumnSpan = '2';
            }
            return (
              <BlogPreview
                showAuthor={showAuthor}
                showCategories={showCategories}
                showPublishedDate={showPublishedDate}
                showReadTime={showReadTime}
                showPreviewText={showPreviewText}
                key={uuid}
                image={image}
                slug={fullSlug}
                title={blogTitle}
                author={author}
                categories={categories}
                format={format}
                publishedDate={publishedDate}
                readTime={readTime}
                text={text}
                style={msStyles}
              />
            );
          }
        )}
      </Grid>
      <Pagination
        baseSlug={story.full_slug}
        currentPage={currentPage}
        totalPages={countPages}
      />
      <CallToActionBackground />
    </Wrapper>
  );
};

export default BlogAggregatePage;
